<script setup>
import { computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useForm, useField } from 'vee-validate'
import Loading from '../../../../components/Loading.vue'
import Box from '../../../../components/Box'
import Select from '../../../../components/Select'
import { castAttributesForSnakeCase } from '../../../../modules/string_converter'
import {
  KkTextField,
  KkValidationError,
  KkFormLabel,
  KkRadioGroup,
  KkCheckbox,
  KkIcon
} from '../../../../components/base'
import { KkInfo, KkListWithMenu } from '../../../../components/ui'
import { KkConditionForm } from '../../../../components/domains/mapionBiz/'
import StatusBadge from '../../../../components/StatusBadge'
const store = useStore()
const router = useRouter()
const { t } = useI18n()
const searchGroup = computed(() => store.state.companiesMapionBizSearchGroups.data)
const searchGroupLoading = computed(() => store.state.companiesMapionBizSearchGroups.loading)
const readMapionBizSearchGroup = (searchGroupId) => store.dispatch('companiesMapionBizSearchGroups/readMapionBizSearchGroup', searchGroupId)
const createMapionBizSearchGroup = (payload) => store.dispatch('companiesMapionBizSearchGroups/createMapionBizSearchGroup', payload)
const updateMapionBizSearchGroup = (searchGroupId, payload) => store.dispatch('companiesMapionBizSearchGroups/updateMapionBizSearchGroup', { searchGroupId, payload })
const updateMapionBizSearchGroupConditionStores = (payload) => store.dispatch('companiesMapionBizSearchGroupsConditionStores/updateMapionBizSearchGroupConditionStores', { searchGroupId: search_group_id, payload: payload })
const isDisplayIcon = computed(() => ['checkboxIcon', 'radioIcon'].includes(state.selectType))
const { company_uuid, search_group_id } = useRoute().params
const OPEN_STATUS = 1
const CLOSE_STATUS = 2
const state = reactive({
  isPublished: true,
  isMappingIcon: false,
  conditions: [],
  createMode: false,
  editMode: false,
  editIndex: 0,
  selectType: 'checkboxIcon',
  openEditConditionIndex: null
})
const schema = {
  name: 'required|max:100',
}

const { handleSubmit, meta, setValues } = useForm({
  validationSchema: schema,
  initialValues: {
    name: '',
    conditionType: 'and',
  },
})


const { value: name, errorMessage: nameError, handleChange: nameHandleChange } = useField('name', undefined, { label: t('pages.companies.mapionBizSearchGroupsForm.basicSetting.name') })
const { value: conditionType, handleChange: conditionTypeHandleChange } = useField('conditionType', undefined, { label: t('pages.companies.mapionBizSearchGroupsForm.basicSetting.conditionType.label') })

const initData = () => {
  setValues({
    name: searchGroup.value.name,
    conditionType: searchGroup.value.conditionType,
  })
  state.conditions = searchGroup.value.conditions.map(v => {
    const result = {}
    result.id = v.id
    result.name = v.name
    result.isPublished = v.isPublished
    result.url = v.icon
    return result
  })
  state.isPublished = searchGroup.value.isPublished
  state.isMappingIcon = searchGroup.value.isMappingIcon
  state.selectType = `${ searchGroup.value.selectType }${ state.conditions.some(v => v.url) ? 'Icon' : '' }`
}

const conditionTypeOptions = [
  {
    id: 'and',
    value: 'and',
    text: t('pages.companies.mapionBizSearchGroupsForm.basicSetting.conditionType.and'),
  },
  {
    id: 'or',
    value: 'or',
    text: t('pages.companies.mapionBizSearchGroupsForm.basicSetting.conditionType.or'),
  },
]

const selectTypeOptions = {
  [t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.selectType.checkboxIcon')]: 'checkboxIcon',
  [t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.selectType.checkbox')]: 'checkbox',
  [t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.selectType.radioIcon')]: 'radioIcon',
  [t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.selectType.radio')]: 'radio',
}

const menuList =   [
  {
    type: 'edit',
    text: t('labels.edit')
  },
  {
    type: 'moveTop',
    text: t('labels.moveTop')
  },
  {
    type: 'moveOneUp',
    text: t('labels.moveOneUp')
  },
  {
    type: 'moveOneDown',
    text: t('labels.moveOneDown')
  },
  {
    type: 'moveBottom',
    text: t('labels.moveBottom')
  },
  {
    type: 'delete',
    text: t('labels.delete')
  },
]

const saveConditions = (value) => {
  if(state.editMode) {
    state.conditions[state.editIndex] = value
  } else {
    state.conditions.push(value)
  }
  resetEditConditions()
}

const addCondition = () => {
  state.createMode = true
}

const resetEditConditions = () => {
  state.createMode = false
  state.editMode = false
  state.editIndex = 0
}

const handleClickMenu = ({ type, index }) => {
  if(type === 'edit') {
    state.editMode = true
    state.editIndex = index
    return
  }
  if (state.conditions.length === 1 && type !== 'delete') return
  if (type === 'moveOneUp' && index === 0) return
  if (type === 'moveBottom' && index === state.conditions.length - 1) return
  const condition = state.conditions[index]
  state.conditions.splice(index, 1)
  switch (type) {
  case 'moveTop':
    state.conditions.unshift(condition)
    break
  case 'moveOneUp':
    if (index === 0) return
    state.conditions.splice(index - 1, 0, condition)
    break
  case 'moveOneDown':
    state.conditions.splice(index + 1, 0, condition)
    break
  case 'moveBottom':
    state.conditions.push(condition)
    break
  case 'delete':
    break
  }
}

const handleSave = handleSubmit(async (value) => {
  if(!isDisplayIcon.value) {
    state.conditions.forEach(v => v.icon = null)
  }
  const selectType = ['radio', 'radioIcon'].includes(state.selectType) ? 'radio' : 'checkbox'
  const payload = {
    search_group: {
      name: value.name,
      is_published: state.isPublished,
      is_mapping_icon: state.isMappingIcon && state.selectType === 'radioIcon',
      select_type: selectType,
      condition_type: value.conditionType,
      conditions: Object.values(castAttributesForSnakeCase(state.conditions))
    }
  }
  let isSuccess = false
  if(search_group_id) {
    let storeIds = []
    searchGroup.value.conditions.forEach(v => storeIds = storeIds.concat(v.stores.map(s => s.uuid)))
    const set = new Set(storeIds)
    if(Array.from(set).length !== storeIds.length && selectType === 'radio') {
      if (!confirm(t('pages.companies.mapionBizSearchGroupsForm.confirm'))) return
      const params = searchGroup.value.conditions.map(v => ({ id: v.id, store_uuids: [] }))
      const isSuccessStore = await updateMapionBizSearchGroupConditionStores({ conditions: params })
      const isSuccessGroup = await updateMapionBizSearchGroup(search_group_id, payload)
      isSuccess = isSuccessGroup && isSuccessStore
    } else {
      isSuccess = await updateMapionBizSearchGroup(search_group_id, payload)
    }
  } else {
    isSuccess = await createMapionBizSearchGroup(payload)
  }
  if(isSuccess) router.push({ name: 'CompaniesMapionBizSearchGroups', params: { company_uuid: company_uuid } })
})

const validation = computed(() => {
  let valid = meta.value.valid && state.conditions.length !== 0 && !state.editMode && !state.createMode
  if(isDisplayIcon.value) {
    const hasICon = state.conditions.every(v => v.url)
    valid = valid && hasICon
  }
  return valid
})

const load = async () => {
  await readMapionBizSearchGroup(search_group_id)
  initData()
}

if(search_group_id) load()
</script>

<template>
  <div>
    <Loading v-if="searchGroupLoading" />
    <div v-else class="p-box">
      <div class="p-box__header">
        <div class="backToIndex">
          <router-link class="c-button__item c-button__item--outline" :to="{ name: 'CompaniesMapionBizSearchGroups', params: { company_uuid: company_uuid } }">
            {{ $t("pages.companies.mapionBizSearchGroupsForm.backToIndex") }}
          </router-link>
        </div>
      </div>
      <div class="p-box__body">
        <div class="c-grid">
          <div class="c-grid__items">
            <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__7 c-grid__item__l__7">
              <Box>
                <template #header>
                  {{ $t('pages.companies.mapionBizSearchGroupsForm.basicSetting.name') }}
                </template>
                <template #body>
                  <div class="formContainer">
                    <KkFormLabel :text="`${ t('pages.companies.mapionBizSearchGroupsForm.basicSetting.name') }(${ $t('labels.countCharacters', { count: name.length, max: 100 }) })${ t('labels.requiredLabel') }`" />
                    <KkTextField :model-value="name" class="c-textField" :placeholder="$t('pages.companies.mapionBizSearchGroupsForm.basicSetting.placeholder')" @update:model-value="nameHandleChange" />
                    <p>{{ $t('pages.companies.mapionBizSearchGroupsForm.basicSetting.example') }}</p>
                    <KkValidationError :text="nameError" />
                  </div>
                  <div>
                    <KkFormLabel :text="$t('pages.companies.mapionBizSearchGroupsForm.basicSetting.conditionType.label')" />
                    <KkRadioGroup
                      :model-value="conditionType"
                      :items="conditionTypeOptions"
                      class="c-radio__input"
                      :name="$t('pages.companies.mapionBizSearchGroupsForm.basicSetting.conditionType.label')"
                      @update:model-value="conditionTypeHandleChange"
                    />
                  </div>
                </template>
              </Box>
              <Box>
                <template #header>
                  {{ $t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.name') }}
                </template>
                <template #body>
                  <div class="formContainer">
                    <KkFormLabel :text="$t('pages.companies.mapionBizSearchGroupsForm.conditionSetting.conditionType')" />
                    <Select v-model="state.selectType" :options="selectTypeOptions" />
                  </div>
                  <KkConditionForm v-if="state.conditions.length === 0" :is-display-icon="isDisplayIcon" @save="saveConditions" @cancel="resetEditConditions" />
                  <template v-for="( condition, i ) in state.conditions"
                            :key="i"
                  >
                    <KkConditionForm
                      v-if="state.editMode && state.editIndex == i"
                      :id="condition.id"
                      :name="condition.name"
                      :icon="condition.url"
                      :is-published="condition.isPublished"
                      :is-display-icon="isDisplayIcon"
                      @save="saveConditions"
                      @cancel="resetEditConditions"
                    />
                    <KkListWithMenu
                      v-else
                      class="conditionList"
                      :class="{ 'conditionList--closed': !condition.isPublished }"
                      :menu-list="menuList"
                      :open="state.openEditConditionIndex === i"
                      @on-click-edit-menu="e => handleClickMenu({ type: e, index: i })"
                      @open="state.openEditConditionIndex = i"
                      @close="state.openEditConditionIndex = null"
                    >
                      <div class="conditionList__wrap">
                        <div v-if="condition.url && isDisplayIcon" class="conditionList__image">
                          <img :src="condition.url" alt="">
                        </div>
                        <div class="conditionList__contents">
                          <span class="conditionList__name">{{ condition.name }}</span>
                          <div class="conditionList__status">
                            <StatusBadge :status="condition.isPublished ? OPEN_STATUS : CLOSE_STATUS" />
                            {{ t(`pages.companies.mapionBizSearchGroupsForm.conditionSetting.${ condition.isPublished ? 'open' : 'close' }`) }}
                          </div>
                        </div>
                      </div>
                    </KkListWithMenu>
                  </template>
                  <KkConditionForm v-if="state.createMode" :is-display-icon="isDisplayIcon" @save="saveConditions" @cancel="resetEditConditions" />
                  <button v-show="!state.editMode && !state.createMode && state.conditions.length !== 0" class="addCondition" type="button" @click="addCondition()">
                    <KkIcon name="iconPlusRed" />
                    <span class="addLabel">{{ $t('pages.companies.mapionBizSearchGroupsForm.addLabel') }}</span>
                  </button>
                </template>
              </Box>
            </div>
            <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__5 c-grid__item__l__5">
              <div class="p-box__body">
                <div class="p-box">
                  <div class="p-box__body">
                    <div class="checkContainer">
                      <KkCheckbox
                        id="isSearchGroupsPublished"
                        :model-value="state.isPublished"
                        :label="$t('pages.companies.mapionBizSearchGroupsForm.isPublished')"
                        @change="state.isPublished = !state.isPublished"
                      />
                    </div>
                    <div v-if="state.selectType === 'radioIcon'" class="checkContainer">
                      <KkCheckbox
                        id="isMappingIcon"
                        :model-value="state.isMappingIcon"
                        :label="$t('pages.companies.mapionBizSearchGroupsForm.isMappingIcon')"
                        @change="state.isMappingIcon = !state.isMappingIcon"
                      />
                    </div>
                    <KkInfo v-if="state.selectType === 'radioIcon'" icon="hint">
                      {{ $t('pages.companies.mapionBizSearchGroupsForm.info') }}
                    </KkInfo>
                    <div class="c-button">
                      <router-link
                        :to="{ name: 'CompaniesMapionBizSearchGroups', params: { company_uuid: company_uuid } }"
                        class="c-button__item c-button__item--outline-cancel"
                      >
                        {{ $t('labels.cancel') }}
                      </router-link>
                      <button
                        class="c-button__item c-button__item--primary"
                        :class="{'c-button__item--disabled': !validation}" :disabled="!validation"
                        @click="handleSave"
                      >
                        {{ $t('labels.save') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';
.formContainer {
  margin-bottom: 10px;
}
.checkContainer {
  padding: 24px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  margin-bottom: 10px;
}
.addCondition {
  margin-top: 32px;
  height: 160px;
  width: 100%;
  color: $color-accent-primary;
  background: $color-base-i;
  border-radius: 4px;
  text-align: center;
    svg {
    display: block;
    width: 32px;
    height: 32px;
    margin: 0 auto;
  }
}
.conditionList{
  margin-top: 15px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  &__wrap {
    display: flex;
  }
  &__title {
    font-weight: bold;
  }
  &__name {
    overflow-wrap: break-word;
  }
  &__contents {
    width: 70%;
  }
  &__image {
    margin-right: 10px;
    img {
      width: 60px;
      height: 60px;
    }
  }
  &--closed {
    background: $color-base-i;
    img {
      filter: grayscale(1) opacity(.4);
    }
    .conditionList__name {
      color: #ababab;
    }
  }
}
.addLabel {
  color: $color-accent-primary;
}
.backToIndex {
  margin-left: auto;
}
</style>
